import {
  TableBody,
  Table,
  TableContainer,
  TableCell,
  TableRow,
} from '@mui/material';
import { processInlineStyles, BORDER_STYLE_PROPERTIES } from './styleUtils';

interface TableCellElement extends HTMLElement {
  innerHTML: string;
}

function getAttributes(element: HTMLElement) {
  return Array.from(element.attributes).reduce((acc, attr) => {
    if (attr.name === 'style' || attr.name === 'class') {
      return acc;
    }

    let key = attr.name;
    if (key === 'colspan') {
      key = 'colSpan';
    }

    return {
      ...acc,
      [key]: attr.value,
    };
  }, {});
}

function TableCellComponent({ element }: { element: TableCellElement }) {
  const tagName = element.tagName.toUpperCase();
  const isHeader = tagName === 'TH';

  const attributes = getAttributes(element);

  const styles = processInlineStyles(element.getAttribute('style'), {
    removeProperties: BORDER_STYLE_PROPERTIES,
  });

  return (
    <TableCell
      variant={isHeader ? 'head' : 'body'}
      component={isHeader ? 'th' : 'td'}
      dangerouslySetInnerHTML={{ __html: element.innerHTML }}
      {...attributes}
      style={styles}
    />
  );
}

function TableRowComponent({
  element,
  index,
}: {
  element: HTMLElement;
  index: number;
}) {
  return (
    <TableRow key={'row' + index} {...getAttributes(element)}>
      {Array.from(element.children).map((cell, cellIndex) => (
        <TableCellComponent
          key={cellIndex}
          element={cell as TableCellElement}
        />
      ))}
    </TableRow>
  );
}

interface TableRendererProps {
  marginBottom: number;
  paddingTop: number;
  chapterId: string;
  tableElement: HTMLTableElement;
}

export default function TableRenderer({
  marginBottom,
  paddingTop,
  chapterId,
  tableElement,
}: TableRendererProps) {
  const rows = Array.from(tableElement.querySelectorAll('tr'));
  const colgroup = tableElement.querySelector('colgroup');

  return (
    <TableContainer
      sx={{ marginBottom, paddingTop }}
      data-chapter-id={chapterId}
      data-has-chapter-id='true'
    >
      <Table size='small' {...getAttributes(tableElement)}>
        {colgroup && (
          <colgroup dangerouslySetInnerHTML={{ __html: colgroup.innerHTML }} />
        )}
        <TableBody>
          {rows.map((row, index) => (
            <TableRowComponent key={index} element={row} index={index} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
